import React, { useState, useEffect, useRef } from 'react';
import API from '../utils/API';

const EMPTY_FORM = {
    type: 'legislative',
    title: '',
    agency: '',
    chamber: 'Assembly',
    chair: '',
    date: '',
    time: '',
    location: '',
    status: 'Scheduled',
    agenda_url: '',
    agenda: '',
};

const STATUSES = ['Scheduled', 'Postponed', 'Cancelled'];
const CHAMBERS = ['Assembly', 'Senate', 'Joint'];

const ORIGIN_LABELS = { manual: 'Added by hand', edited: 'Edited', scraped: 'Scraped' };

// "14:30", "2:30pm", "2:30 PM" -> "2:30 p.m." (the format the scrapers store). Anything else is kept as typed.
const normalizeTime = (text) => {
    const raw = (text || '').trim();
    const m = /^(\d{1,2})(?::(\d{2}))?\s*([ap])?\.?(?:m\.?)?$/i.exec(raw);
    if (!m) return raw;
    const h = parseInt(m[1], 10);
    const min = m[2] || '00';
    let pm;
    if (m[3]) pm = m[3].toLowerCase() === 'p';
    else if (h >= 13 || m[1].length === 2 && m[1][0] === '0') pm = h >= 12;
    else return raw;
    if (h > 23 || parseInt(min, 10) > 59 || (m[3] && h > 12)) return raw;
    return `${h % 12 || 12}:${min} ${pm ? 'p.m.' : 'a.m.'}`;
};

const formatDate = (iso) => {
    const [y, mo, d] = iso.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

export default function AdminHearings() {
    const [hearings, setHearings] = useState([]);
    const [loading, setLoading] = useState(true);
    const formRef = useRef(null);

    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(null);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const [originFilter, setOriginFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [showPast, setShowPast] = useState(false);
    const [confirmDeleteKey, setConfirmDeleteKey] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await API.getAdminHearings();
            setHearings(res.data);
        } catch (err) {
            console.error('Failed to load hearings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const rowKey = (row) => `${row.type}_${row.id}`;
    const setField = (key, value) => {
        setForm(f => ({ ...f, [key]: value }));
        setFormError('');
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditing(null);
        setFormError('');
    };

    const startEdit = (row) => {
        setEditing(row);
        setForm({
            type: row.type,
            title: row.title || '',
            agency: row.agency || '',
            chamber: row.chamber || 'Assembly',
            chair: row.chair || '',
            date: row.date || '',
            time: row.time || '',
            location: row.location || '',
            status: row.status || 'Scheduled',
            agenda_url: row.agenda_url || '',
            agenda: row.agenda || '',
        });
        setFormError('');
        setConfirmDeleteKey(null);
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFormError('');
        const isLeg = form.type === 'legislative';
        const payload = {
            type: form.type,
            title: form.title,
            agency: form.agency,
            chamber: isLeg ? form.chamber : null,
            chair: isLeg ? form.chair : null,
            date: form.date,
            time: normalizeTime(form.time),
            location: form.location,
            status: form.status,
            agenda_url: form.agenda_url,
            agenda: isLeg ? form.agenda : null,
        };
        try {
            if (editing) {
                await API.updateAdminHearing(editing.type, editing.id, payload);
            } else {
                await API.addAdminHearing(payload);
            }
            resetForm();
            await load();
        } catch (err) {
            setFormError(err.response?.data?.msg || 'Failed to save hearing.');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async (row) => {
        setDeleting(true);
        try {
            await API.deleteAdminHearing(row.type, row.id);
            setConfirmDeleteKey(null);
            if (editing && rowKey(editing) === rowKey(row)) resetForm();
            setHearings(prev => prev.filter(h => rowKey(h) !== rowKey(row)));
        } catch (err) {
            console.error('Failed to delete hearing:', err);
        } finally {
            setDeleting(false);
        }
    };

    const isLegislative = form.type === 'legislative';
    const today = todayIso();
    const needle = search.trim().toLowerCase();
    const visible = hearings.filter(h => {
        if (!showPast && h.date < today) return false;
        if (originFilter === 'manual' && h.origin !== 'manual') return false;
        if (originFilter === 'edited' && h.origin !== 'edited') return false;
        if (typeFilter !== 'all' && h.type !== typeFilter) return false;
        if (needle && !`${h.title} ${h.agency} ${h.location || ''}`.toLowerCase().includes(needle)) return false;
        return true;
    });
    const pastCount = hearings.filter(h => h.date < today).length;

    return (
        <div>
            <p className="text-muted small mb-3">
                Every hearing and meeting in the Dashboard Hearings widget that comes from the scrapers can be corrected
                here, and you can add ones the scrapers don't pick up. Corrected and added hearings are kept as you
                entered them when the scrapers next run. Committee hearings from the Legislature's own agendas and
                rulemaking hearings aren't listed.
            </p>

            <form className="ah-form" onSubmit={handleSubmit} ref={formRef}>
                <div className="ah-form-heading">
                    {editing ? 'Edit hearing' : 'Add a hearing'}
                </div>
                {editing && editing.origin !== 'manual' && (
                    <div className="ah-form-note">
                        This hearing was scraped{editing.source_key ? ` (${editing.source_key})` : ''}. Your changes replace the
                        scraped details and are kept when the scraper runs again.
                    </div>
                )}

                <div className="ah-type-toggle">
                    <button
                        type="button"
                        className={`awards-btn ${isLegislative ? 'awards-btn--activate' : ''}`}
                        onClick={() => setField('type', 'legislative')}
                        disabled={!!editing}
                    >
                        Legislative
                    </button>
                    <button
                        type="button"
                        className={`awards-btn ${!isLegislative ? 'awards-btn--activate' : ''}`}
                        onClick={() => setField('type', 'agency')}
                        disabled={!!editing}
                    >
                        Agency
                    </button>
                </div>

                <div className="ah-grid">
                    <div className="ai-field ah-span-2">
                        <label className="ai-field-label">
                            {isLegislative ? 'Hearing title (e.g. Informational Hearing)' : 'Title (e.g. Board Meeting)'}
                        </label>
                        <input
                            className="admin-search-input"
                            type="text"
                            value={form.title}
                            onChange={e => setField('title', e.target.value)}
                            maxLength={isLegislative ? 80 : 300}
                        />
                    </div>

                    <div className="ai-field ah-span-2">
                        <label className="ai-field-label">
                            {isLegislative ? 'Committee / body' : 'Agency / body'}
                        </label>
                        <input
                            className="admin-search-input"
                            type="text"
                            placeholder={isLegislative ? 'e.g. Health Committee' : 'e.g. High-Speed Rail Authority'}
                            value={form.agency}
                            onChange={e => setField('agency', e.target.value)}
                            maxLength={255}
                        />
                    </div>

                    {isLegislative && (
                        <>
                            <div className="ai-field">
                                <label className="ai-field-label">Chamber</label>
                                <select
                                    className="admin-search-input"
                                    value={form.chamber}
                                    onChange={e => setField('chamber', e.target.value)}
                                >
                                    {CHAMBERS.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="ai-field ah-span-3">
                                <label className="ai-field-label">Chair (optional)</label>
                                <input
                                    className="admin-search-input"
                                    type="text"
                                    value={form.chair}
                                    onChange={e => setField('chair', e.target.value)}
                                    maxLength={150}
                                />
                            </div>
                        </>
                    )}

                    <div className="ai-field">
                        <label className="ai-field-label">Date</label>
                        <input
                            className="admin-search-input"
                            type="date"
                            value={form.date}
                            onChange={e => setField('date', e.target.value)}
                        />
                    </div>

                    <div className="ai-field">
                        <label className="ai-field-label">Time</label>
                        <input
                            className="admin-search-input"
                            type="text"
                            placeholder="e.g. 9:30 a.m."
                            value={form.time}
                            onChange={e => setField('time', e.target.value)}
                            onBlur={e => setField('time', normalizeTime(e.target.value))}
                            maxLength={40}
                        />
                    </div>

                    <div className="ai-field">
                        <label className="ai-field-label">Status</label>
                        <select
                            className="admin-search-input"
                            value={form.status}
                            onChange={e => setField('status', e.target.value)}
                        >
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="ai-field ah-span-4">
                        <label className="ai-field-label">Location</label>
                        <input
                            className="admin-search-input"
                            type="text"
                            placeholder="Address, room, or Zoom"
                            value={form.location}
                            onChange={e => setField('location', e.target.value)}
                            maxLength={500}
                        />
                    </div>

                    <div className="ai-field ah-span-4">
                        <label className="ai-field-label">Agenda link</label>
                        <input
                            className="admin-search-input"
                            type="url"
                            placeholder="https://"
                            value={form.agenda_url}
                            onChange={e => setField('agenda_url', e.target.value)}
                            maxLength={isLegislative ? 500 : 600}
                        />
                    </div>

                    {isLegislative && (
                        <div className="ai-field ah-span-4">
                            <label className="ai-field-label">Agenda / subject (optional)</label>
                            <textarea
                                className="admin-search-input"
                                rows={3}
                                value={form.agenda}
                                onChange={e => setField('agenda', e.target.value)}
                            />
                        </div>
                    )}
                </div>

                {formError && <div className="ai-error mb-2">{formError}</div>}

                <div className="ah-form-actions">
                    <button
                        className="awards-save-btn"
                        type="submit"
                        disabled={saving || !form.title.trim() || !form.agency.trim() || !form.date}
                    >
                        {saving ? <i className="bi bi-arrow-repeat admin-spin" /> : editing ? 'Save changes' : 'Add hearing'}
                    </button>
                    {editing && (
                        <button className="awards-btn" type="button" onClick={resetForm}>Cancel</button>
                    )}
                </div>
            </form>

            <div className="ah-filters">
                <input
                    className="admin-search-input ah-search"
                    type="search"
                    placeholder="Search title, agency, or location"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select className="admin-search-input ah-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                    <option value="all">Legislative and agency</option>
                    <option value="legislative">Legislative only</option>
                    <option value="agency">Agency only</option>
                </select>
                <select className="admin-search-input ah-filter" value={originFilter} onChange={e => setOriginFilter(e.target.value)}>
                    <option value="all">All sources</option>
                    <option value="manual">Added by hand</option>
                    <option value="edited">Edited</option>
                </select>
                {pastCount > 0 && (
                    <label className="ah-past-toggle">
                        <input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} />
                        Show {pastCount} past
                    </label>
                )}
            </div>

            {loading ? (
                <div className="admin-loading">Loading…</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Hearing</th>
                                <th>Date / time</th>
                                <th>Location</th>
                                <th>Agenda</th>
                                <th className="admin-th-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.length === 0 ? (
                                <tr><td colSpan={5} className="admin-empty">No hearings found</td></tr>
                            ) : visible.map(row => (
                                <tr key={rowKey(row)} className={editing && rowKey(editing) === rowKey(row) ? 'ai-row-selected' : ''}>
                                    <td data-label="Hearing">
                                        <div>{row.title}</div>
                                        <div className="text-muted small">
                                            {row.agency}{row.type === 'legislative' && ` · ${row.chamber}`}
                                        </div>
                                        <div className="ah-badges">
                                            <span className={`ah-badge ah-badge--${row.origin}`}>{ORIGIN_LABELS[row.origin]}</span>
                                            {row.status !== 'Scheduled' && <span className="ah-badge ah-badge--status">{row.status}</span>}
                                        </div>
                                    </td>
                                    <td data-label="Date / time">
                                        {formatDate(row.date)}
                                        <div className="text-muted small">{row.time || 'Time TBA'}</div>
                                    </td>
                                    <td data-label="Location">{row.location || '—'}</td>
                                    <td data-label="Agenda">
                                        {row.agenda_url ? (
                                            <a href={row.agenda_url} target="_blank" rel="noopener noreferrer">
                                                Link <i className="bi bi-box-arrow-up-right" />
                                            </a>
                                        ) : '—'}
                                    </td>
                                    <td className="admin-td-center" data-label="Actions">
                                        {confirmDeleteKey === rowKey(row) ? (
                                            <div className="ai-delete-confirm">
                                                <span className="ai-delete-confirm-msg">Delete this hearing?</span>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => confirmDelete(row)}
                                                    disabled={deleting}
                                                >
                                                    {deleting ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Yes, delete'}
                                                </button>
                                                <button className="awards-btn ai-action-btn" onClick={() => setConfirmDeleteKey(null)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="ai-action-group">
                                                <button
                                                    className="awards-btn awards-btn--edit ai-action-btn"
                                                    onClick={() => startEdit(row)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil me-1" />Edit
                                                </button>
                                                {row.origin === 'manual' && (
                                                    <button
                                                        className="awards-btn awards-btn--delete ai-action-btn"
                                                        onClick={() => setConfirmDeleteKey(rowKey(row))}
                                                        title="Delete"
                                                    >
                                                        <i className="bi bi-trash me-1" />Delete
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
